/**
 * The one form an e-mail address is stored and compared in: trimmed and lowercase.
 *
 * Seiri treats addresses as case-insensitive - `Jana@Example.com` and `jana@example.com` are the same person - and
 * stores them lowercase so a plain `=` matches (docs/clients.md). The HTTP schemas (`emailField`), the entity columns
 * (`lowercaseEmail`) and the few hand-written comparisons all go through this.
 */
export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase()
}

/** Whether two addresses are the same, ignoring case - for values that may predate lowercase storage (old tokens). */
export function sameEmail(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && normalizeEmail(a) === normalizeEmail(b)
}

/** The part after the last `@`, normalised; empty when there is none. */
export function emailDomain(email: string): string {
  const normal = normalizeEmail(email)
  const at = normal.lastIndexOf('@')
  return at === -1 ? '' : normal.slice(at + 1)
}

/**
 * Reads a list of domains from configuration: separated by commas, whitespace or newlines, each normalised to
 * lowercase with any leading `@` or `.` dropped, blanks and duplicates removed.
 */
export function parseDomainList(raw: string | null | undefined): string[] {
  const domains = (raw ?? '')
    .split(/[\s,]+/)
    .map((domain) => domain.toLowerCase().replace(/^[@.]+/, ''))
    .filter(Boolean)
  return [...new Set(domains)]
}

/**
 * Seiri's own test inboxes. Staging delivers only to Mailinator (`API_MAILGUN_MODE=mailinator`), so QA registers with
 * these even where `mailinator.com` is blocked; every other Mailinator address stays refused.
 */
const SEIRI_TEST_INBOX = /^seiri-test-.+@mailinator\.com$/

/** Whether the address is one of Seiri's test inboxes, `seiri-test-<anything>@mailinator.com`. */
export function isSeiriTestInbox(email: string): boolean {
  return SEIRI_TEST_INBOX.test(normalizeEmail(email))
}

/**
 * Whether the address belongs to one of the domains, or to a subdomain of one: blocking `yopmail.com` also blocks
 * `x@mail.yopmail.com`, but not `x@notyopmail.com`. A Seiri test inbox is never blocked.
 */
export function isEmailDomainBlocked(email: string, blockedDomains: readonly string[]): boolean {
  const domain = emailDomain(email)
  if (!domain || isSeiriTestInbox(email)) return false
  return blockedDomains.some((blocked) => domain === blocked || domain.endsWith(`.${blocked}`))
}
